// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import { itemFieldSchema } from '@/items/api/types';
import { z } from 'zod';

/**
 * Schema for the update item field API response.
 * Includes the updated field data and metadata about the request.
 */
const updateItemFieldResponseSchema = z
  .object({ data: itemFieldSchema, metadata: responseMetadataSchema })
  .transform(({ data, ...response }) => ({ ...response, field: data }));

/**
 * Parameters for updating an item field.
 * Currently only supports updating the display label.
 */
interface UpdateItemFieldParameters {
  label: string;
}

/**
 * Updates an existing field on an item.
 * Currently only supports updating the display label of the field.
 *
 * @param itemKey - The key of the item that owns the field
 * @param fieldKey - The key of the field to update
 * @param parameters - The new field configuration
 * @returns A promise that resolves to the updated field data and request metadata
 */
export async function updateItemField(
  itemKey: string,
  fieldKey: string,
  parameters: UpdateItemFieldParameters
) {
  const url = `/v1/catalog/item/${itemKey}/field/${fieldKey}`;
  const response = await api.patch(url, parameters);

  return parseResponse(updateItemFieldResponseSchema, response.data, {
    schemaName: 'UpdateItemField',
    endpoint: url,
  });
}
